/**
 * Customisations (D219): the agents, instructions, hooks, plugins and marketplaces the organisation
 * publishes for every workbench, the entries people submit from their workbench for approval, and
 * what each machine actually has. A page of its own under Rules rather than a tab of the Catalogue:
 * it has an approval queue and a fleet view the Catalogue does not, operators read the machine view
 * without needing the catalogue's editor, and a hook or plugin is set up in fields, not Markdown.
 */

import { HOOK_EVENT_WORDS, HOOK_EVENTS, designSystemNotes, toolServerEntrySchema, type CatalogueEntry, type CatalogueSubmission, type DesignSystemContent, type HookContent, type MarketplaceContent, type PluginContent } from '@aioe/shared';
import { CircleQuestion, Display, FileText, Person, Plus, Puzzle, ShoppingBag, Thunderbolt } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useState, useSyncExternalStore, type ReactNode } from 'react';

import { problemText, routes, type CatalogueSubmissionsSetting, type CustomisationPresence, type CustomisationsView } from '../api/client.ts';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark, Tick } from '../components/ui.tsx';
import { FormField, FormSheet } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { VIEW_TONES, type ArtTone } from '../components/ui/Art.tsx';
import { licenceWords, provenanceWords, spreadWords, standing } from '../components/designSystemWords.ts';
import { subscribeConsoleDesignSystem, tryingOn, tryOnDesignSystem } from '../orgDesignSystem.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { listCount, NoMatch, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { blankFit, FitFields, fitDraftOf, fitOfDraft, fitSentence, type FitDraft } from '../components/FitFields.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { EvaluateDialog, SkillEvidence } from '../components/SkillEvidence.tsx';
import { isWebService, ScanFindings, ServerVerification, VerifyDialog } from '../components/CatalogueChecks.tsx';
import { originWords, similarWords } from '../components/skillEvidenceWords.ts';

/** The tool names a tool server submission declares (an empty list when it declares none). */
const declaredTools = (content: unknown): string[] => {
  const parsed = toolServerEntrySchema.safeParse(content);
  return parsed.success ? (parsed.data.tools ?? []) : [];
};

type Kind = 'agent' | 'instruction' | 'hook' | 'plugin' | 'marketplace';
type Runtime = 'claude-code' | 'copilot' | 'codex';
type HookEvent = (typeof HOOK_EVENTS)[number];

const KINDS: Kind[] = ['hook', 'plugin', 'agent', 'instruction', 'marketplace'];
const KIND_TEXT: Record<Kind, string> = { agent: 'Agents', instruction: 'Instructions', hook: 'Hooks', plugin: 'Plugins', marketplace: 'Marketplaces' };
const KIND_ICON: Record<Kind, ReactNode> = { agent: <Person />, instruction: <FileText />, hook: <Thunderbolt />, plugin: <Puzzle />, marketplace: <ShoppingBag /> };
const KIND_ONE: Record<Kind, string> = { agent: 'Agent', instruction: 'Instruction', hook: 'Hook', plugin: 'Plugin', marketplace: 'Marketplace' };
const KIND_ARTICLE: Record<string, string> = { agent: 'an agent', instruction: 'an instruction', hook: 'a hook', plugin: 'a plugin', marketplace: 'a marketplace', skill: 'a skill', prompt: 'a prompt', role: 'a role', team: 'a team', document: 'a document template', mcp: 'a tool server', 'design-system': 'a design system', model: 'a local model', evaluation: 'an evaluation dataset' };
const RUNTIME_TEXT: Record<Runtime, string> = { 'claude-code': 'Claude Code', copilot: 'Copilot', codex: 'Codex' };
const TOOL_EVENTS: HookEvent[] = ['before-tool', 'after-tool', 'after-tool-failure'];
const DELIVERY_TEXT = { offered: 'Offered: people add it', installed: 'Installed on every machine' } as const;
const SETTING_TEXT: Record<CatalogueSubmissionsSetting, string> = { admins: 'Administrators only; others wait for approval', members: 'Everyone in the organisation', off: 'Nobody: publish here in the console' };
const CODEX_NOTE = 'Codex runs a hook only after the person trusts it in Codex (/hooks).';

const runtimesText = (rs: string[]) => rs.map((r) => RUNTIME_TEXT[r as Runtime] ?? r).join(', ');
const isCustomisation = (k: string): k is Kind => (KINDS as string[]).includes(k);

/** The front matter's simple `key: value` lines, and the body after it. */
function frontMatter(md: string): { head: Record<string, string>; body: string } {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(md);
  if (!m) return { head: {}, body: md };
  const head: Record<string, string> = {};
  for (const line of m[1]!.split(/\r?\n/)) {
    const kv = /^([A-Za-z][\w-]*)\s*:\s*(.*)$/.exec(line);
    if (kv) head[kv[1]!] = kv[2]!.trim().replace(/^(['"])(.*)\1$/, '$2');
  }
  return { head, body: md.slice(m[0].length) };
}

/** One line in plain words: what it is, for which runtimes, and how it reaches machines. */
function entryLine(e: CatalogueEntry): string {
  const c = e.content as Record<string, unknown> | string;
  const reach = (d: unknown) => (d === 'installed' ? 'installed on every machine' : 'offered to people');
  switch (e.kind) {
    case 'hook': {
      const h = c as HookContent;
      return [HOOK_EVENT_WORDS[h.event] ?? h.event, h.matcher ? `for ${h.matcher}` : '', runtimesText(h.runtimes), reach(h.delivery)].filter(Boolean).join(' · ');
    }
    case 'plugin': {
      const p = c as PluginContent;
      return `${p.plugin} from ${p.marketplace} · ${runtimesText(p.runtimes)} · ${reach(p.delivery)}`;
    }
    case 'marketplace': {
      const m = c as MarketplaceContent;
      return `${m.name} · ${m.source} · ${runtimesText(m.runtimes)}`;
    }
    case 'agent': {
      const { head } = frontMatter(typeof c === 'string' ? c : '');
      return [head.description, head.model ? `model ${head.model}` : '', fitSentence(e.fit)].filter(Boolean).join(' · ') || 'An agent';
    }
    case 'instruction': {
      const { head } = frontMatter(typeof c === 'string' ? c : '');
      return head.appliesTo ? `Applies to ${head.appliesTo}` : 'Applies everywhere';
    }
    default:
      return e.summary ?? '';
  }
}

const namesCodex = (e: Pick<CatalogueEntry, 'kind' | 'content'>) => e.kind === 'hook' && ((e.content as HookContent).runtimes ?? []).includes('codex');

interface Draft {
  fit: FitDraft;
  id?: string;
  kind: Kind;
  name: string;
  summary: string;
  body: string;
  description: string;
  tools: string;
  model: string;
  appliesTo: string;
  event: HookEvent;
  matcher: string;
  command: string;
  timeout: string;
  runtimes: Runtime[];
  delivery: 'offered' | 'installed';
  plugin: string;
  marketplace: string;
  marketName: string;
  source: string;
}

const blank = (kind: Kind): Draft => ({ fit: blankFit, kind, name: '', summary: '', body: '', description: '', tools: '', model: '', appliesTo: '', event: 'before-tool', matcher: '', command: '', timeout: '', runtimes: ['claude-code'], delivery: 'offered', plugin: '', marketplace: '', marketName: '', source: '' });

function draftOf(e: CatalogueEntry): Draft {
  const d = { ...blank(e.kind as Kind), fit: fitDraftOf(e.fit), id: e.id, name: e.name, summary: e.summary ?? '' };
  const c = e.content as Record<string, unknown>;
  if (e.kind === 'agent' || e.kind === 'instruction') {
    const { head, body } = frontMatter(typeof e.content === 'string' ? e.content : '');
    return { ...d, body, description: head.description ?? '', tools: (head.tools ?? '').replace(/^\[|\]$/g, ''), model: head.model ?? '', appliesTo: head.appliesTo ?? '' };
  }
  if (e.kind === 'hook') return { ...d, event: c.event as HookEvent, matcher: (c.matcher as string) ?? '', command: c.command as string, timeout: c.timeout ? String(c.timeout) : '', runtimes: c.runtimes as Runtime[], delivery: c.delivery as Draft['delivery'] };
  if (e.kind === 'plugin') return { ...d, plugin: c.plugin as string, marketplace: c.marketplace as string, runtimes: c.runtimes as Runtime[], delivery: c.delivery as Draft['delivery'] };
  return { ...d, marketName: c.name as string, source: c.source as string, runtimes: c.runtimes as Runtime[] };
}

function contentOf(d: Draft): unknown {
  switch (d.kind) {
    case 'agent': {
      const tools = d.tools.split(',').map((t) => t.trim()).filter(Boolean);
      const head = [`name: ${d.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`, `description: ${d.description.trim()}`, tools.length ? `tools: [${tools.join(', ')}]` : '', d.model.trim() ? `model: ${d.model.trim()}` : ''].filter(Boolean);
      return `---\n${head.join('\n')}\n---\n\n${d.body.trim()}\n`;
    }
    case 'instruction':
      return d.appliesTo.trim() ? `---\nappliesTo: "${d.appliesTo.trim()}"\n---\n\n${d.body.trim()}\n` : `${d.body.trim()}\n`;
    case 'hook':
      return { event: d.event, ...(TOOL_EVENTS.includes(d.event) && d.matcher.trim() ? { matcher: d.matcher.trim() } : {}), command: d.command.trim(), ...(d.timeout.trim() ? { timeout: Number(d.timeout) } : {}), runtimes: d.runtimes, delivery: d.delivery };
    case 'plugin':
      return { plugin: d.plugin.trim(), marketplace: d.marketplace, runtimes: d.runtimes.filter((r) => r !== 'codex'), delivery: d.delivery };
    case 'marketplace':
      return { name: d.marketName.trim(), source: d.source.trim(), runtimes: d.runtimes.filter((r) => r !== 'codex') };
  }
}

const newId = (d: Draft, taken: Set<string>) => {
  const base = `${d.kind}-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'entry'}`;
  let id = base;
  for (let i = 2; taken.has(id); i++) id = `${base}-${i}`;
  return id;
};

export function CustomisationsPage() {
  const tone = VIEW_TONES.customisations!;
  const cat = useLoad(() => routes.customisationCatalogue(), []);
  const subs = useLoad(() => routes.catalogueSubmissions(), [], 60_000);
  // One machine is enough here: the lists of what each plugin and hook is on stay whole, and "Each machine" pages itself.
  const machines = useLoad(() => routes.customisations({ limit: 1 }), [], 60_000);
  const markets = useLoad(() => routes.publicMarketplaces().catch(() => []), []);
  const org = useLoad(() => routes.organisationProfile(), []);
  const [draft, setDraft] = useState<Draft>();
  const [removing, setRemoving] = useState<CatalogueEntry>();
  const [error, setError] = useState<string>();
  const [tab, setTab] = useState<'published' | 'machines' | 'decided'>('published');

  const entries = (cat.data?.entries ?? []).filter((e) => isCustomisation(e.kind));
  const pending = subs.data?.pending ?? [];
  const decided = subs.data?.decided ?? [];
  const view = machines.data;
  const gaps = (view?.missing ?? []).reduce((n, m) => n + m.machines.length, 0);
  const reload = () => {
    cat.reload();
    subs.reload();
    machines.reload();
  };

  const installed = entries.filter((e) => (e.content as { delivery?: string }).delivery === 'installed').length;
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<Puzzle />}
        title="Customisations"
        subtitle="Agents, instructions, hooks, plugins and marketplaces the organisation gives every workbench, what people submit from theirs, and what each machine has."
        actions={
          <Button size="sm" variant="primary" onPress={() => setDraft(blank('hook'))}>
            <Plus /> Add a customisation
          </Button>
        }
      />
      <Explainer id="customisations" tone={tone} icon={<Puzzle />} title="How customisations work" summary="Published here or submitted from a workbench, offered to people or installed on every machine">
        <p>A customisation changes how the agents on a workbench behave: a hook runs a command before or after a tool, a plugin adds commands and agents from a marketplace, an agent is a specialist with its own instructions, and an instruction is a rule agents follow in matching files.</p>
        <p>Each one is either offered, so people add it themselves, or installed on every machine. People can also submit one from their workbench; it waits here for an administrator unless the organisation lets everyone publish.</p>
        <p>On machines shows what each workbench really has, and what has not arrived yet.</p>
      </Explainer>
      <PartBoundary key="customisations" name="Customisations" tone={tone}>
        <ErrorNote error={cat.error ?? subs.error ?? machines.error ?? error} />
        <div className="art-stats">
          <Stat tone={tone} icon={<Puzzle />} label="Published" value={cat.data ? entries.length : undefined} detail={entries.length ? `${installed} installed on every machine` : 'Nothing yet'} />
          <Stat tone={tone} icon={<CircleQuestion />} label="Waiting for approval" value={subs.data ? pending.length : undefined} detail={pending.length ? 'Submitted from workbenches' : 'Nothing waiting'} needsYou={pending.length > 0} />
          <Stat tone={tone} icon={<Display />} label="Not installed yet" value={view ? gaps : undefined} detail={view ? `On ${view.machines.reporting} reporting ${view.machines.reporting === 1 ? 'machine' : 'machines'}${view.machines.silent ? `, ${view.machines.silent} older` : ''}` : ''} onPress={() => setTab('machines')} selected={tab === 'machines'} />
        </div>

        {pending.length > 0 && <Queue items={pending} onChange={reload} />}

        <Segment aria-label="Customisations view" size="sm" selectedKey={tab} onSelectionChange={(k) => setTab(String(k) as typeof tab)} className="w-fit max-w-full">
          <Segment.Item id="published">Published</Segment.Item>
          <Segment.Item id="machines">On machines</Segment.Item>
          <Segment.Item id="decided">Decided</Segment.Item>
        </Segment>

        {tab === 'published' && (
          <>
            {cat.data && entries.length === 0 ? (
              <Empty tone={tone} icon={<Puzzle />} title="No customisations yet" body="Add a hook, a plugin, an agent or an instruction; workbenches pick it up at their next refresh." action={<Button size="sm" variant="primary" onPress={() => setDraft(blank('hook'))}>Add a customisation</Button>} />
            ) : null}
            {KINDS.filter((k) => entries.some((e) => e.kind === k)).map((k) => (
              <ListGroup key={k} title={KIND_TEXT[k]} count={entries.filter((e) => e.kind === k).length}>
                {entries
                  .filter((e) => e.kind === k)
                  .map((e) => (
                    <ListRow
                      key={e.id}
                      icon={KIND_ICON[k]}
                      title={e.name}
                      description={
                        <>
                          <span className="block">{entryLine(e)}</span>
                          {e.summary && k !== 'agent' ? <span className="line-clamp-2">{e.summary}</span> : null}
                          {namesCodex(e) ? <span className="block">{CODEX_NOTE}</span> : null}
                        </>
                      }
                      compactControl
                      control={
                        <RowMenu
                          label={`More for ${e.name}`}
                          items={[
                            { id: 'edit', label: 'Edit…' },
                            { id: 'remove', label: 'Remove…', danger: true },
                          ]}
                          onAction={(id) => (id === 'edit' ? setDraft(draftOf(e)) : setRemoving(e))}
                        />
                      }
                    />
                  ))}
              </ListGroup>
            ))}
            <ListGroup title="Who may publish">
              <ListRow
                title="Publishing from a workbench"
                description="Who may publish from their workbench without an administrator’s approval. Administrators always may, here in the console."
                control={
                  <ChoiceSelect
                    label="Who may publish"
                    className="w-80 max-w-full"
                    value={org.data?.catalogueSubmissions ?? 'admins'}
                    options={SETTING_TEXT}
                    onChange={(v) => {
                      setError(undefined);
                      void routes.setCatalogueSubmissions(v).then(org.reload, (e) => setError(problemText(e)));
                    }}
                  />
                }
              />
            </ListGroup>
          </>
        )}

        {tab === 'machines' && <Machines tone={tone} v={view} />}

        {tab === 'decided' && (
          <ListGroup
            title="Decided"
            count={decided.length}
            description="The last 50 submissions, newest first."
            empty={subs.data && !decided.length ? <Empty tone={tone} icon={<Puzzle />} title="Nothing decided yet" body="Submissions from workbenches show here once someone approves or declines them." /> : undefined}
          >
            {decided.map((s) => (
              <ListRow
                key={s.id}
                title={
                  <>
                    {s.submittedByName ?? s.submittedBy} submitted {KIND_ARTICLE[s.entry.kind] ?? s.entry.kind}, {s.entry.name}
                  </>
                }
                description={[timeAgo(s.decidedAt ?? s.submittedAt), s.reason].filter(Boolean).join(' · ')}
                status={<StatusMark state={s.state === 'published' ? 'connected' : 'disabled'} label={s.state === 'published' ? 'Published' : 'Declined'} />}
              />
            ))}
          </ListGroup>
        )}
      </PartBoundary>

      {draft && (
        <Editor
          draft={draft}
          setDraft={setDraft}
          entries={cat.data?.entries ?? []}
          markets={[...(markets.data ?? []).map((m) => ({ name: m.name, label: m.label })), ...entries.filter((e) => e.kind === 'marketplace').map((e) => ({ name: (e.content as MarketplaceContent).name, label: e.name }))]}
          onSaved={() => {
            setDraft(undefined);
            reload();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(removing)}
        title={`Remove ${removing?.name ?? ''}?`}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={async () => {
          try {
            await routes.deleteCatalogue(removing!.id);
            setRemoving(undefined);
            reload();
          } catch (e) {
            setRemoving(undefined);
            setError(problemText(e));
          }
        }}
      >
        Workbenches stop offering it at their next refresh. What a person already added stays on their machine until they remove it.
      </ConfirmDialog>
    </PageColumn>
  );
}

/** A submitted design system in words: its version and tones, where it came from, its fonts' licences and what changed. */
function DesignSystemLines({ entry }: { entry: CatalogueEntry }) {
  const c = entry.content as Partial<DesignSystemContent> | null;
  const notes = designSystemNotes(entry.content);
  const lines = [
    [`Version ${c?.version ?? entry.version ?? ''}`, `${c?.tones?.length ?? 0} ${c?.tones?.length === 1 ? 'tone' : 'tones'}`, `${c?.logos?.length ?? 0} ${c?.logos?.length === 1 ? 'logo' : 'logos'}`].join(' · '),
    [provenanceWords(notes.origin), licenceWords(c?.fonts)].filter(Boolean).join(' · '),
    notes.changes ? `What changed: ${notes.changes}` : '',
  ].filter(Boolean);
  return (
    <>
      {lines.map((l) => (
        <span key={l} className="block break-words">
          {l}
        </span>
      ))}
    </>
  );
}

/** Pending submissions: approve, or decline with a reason the person reads. */
function Queue({ items, onChange }: { items: CatalogueSubmission[]; onChange: () => void }) {
  // A design system shared from a workbench can be worn here before it is approved (this browser only).
  const trying = useSyncExternalStore(subscribeConsoleDesignSystem, tryingOn);
  const [declining, setDeclining] = useState<string>();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [evaluating, setEvaluating] = useState<CatalogueSubmission>();
  const [verifying, setVerifying] = useState<CatalogueSubmission>();
  const run = async (id: string, f: () => Promise<unknown>) => {
    setBusy(id);
    setError(undefined);
    try {
      await f();
      setDeclining(undefined);
      setReason('');
      onChange();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <ListGroup title="Waiting for approval" count={items.length} description="Submitted from workbenches. Approving publishes it to every workbench; a declined one goes back with your reason.">
      {items.map((s) => {
        const e = { ...s.entry, id: s.id } as CatalogueEntry;
        const blocked = s.scan?.level === 'dangerous' || (!s.verified?.stale && (s.verified?.undeclared.length ?? 0) > 0);
        return (
          <ListRow
            key={s.id}
            icon={isCustomisation(s.entry.kind) ? KIND_ICON[s.entry.kind] : <Puzzle />}
            title={
              <>
                {s.submittedByName ?? s.submittedBy} submitted {KIND_ARTICLE[s.entry.kind] ?? s.entry.kind}, {s.entry.name}
                {s.replaces ? `, a new version of ${s.replaces}` : ''}
              </>
            }
            description={
              <>
                <span className="block">{[isCustomisation(s.entry.kind) ? entryLine(e) : s.entry.summary, timeAgo(s.submittedAt)].filter(Boolean).join(' · ')}</span>
                {s.entry.summary && isCustomisation(s.entry.kind) ? <span className="block">{s.entry.summary}</span> : null}
                {s.entry.kind === 'design-system' ? <DesignSystemLines entry={e} /> : null}
                {namesCodex(e) ? <span className="block">{CODEX_NOTE}</span> : null}
                {originWords(s) ? <span className="block">{originWords(s)}</span> : null}
                {similarWords(s.similar) ? <span className="block text-warning">{similarWords(s.similar)}</span> : null}
              </>
            }
            control={
              declining !== s.id ? (
                <>
                  <Button size="sm" variant="secondary" isPending={busy === s.id} isDisabled={blocked || (Boolean(busy) && busy !== s.id)} onPress={() => void run(s.id, () => routes.approveSubmission(s.id))}>
                    Approve
                  </Button>
                  <RowMenu
                    label={`More for ${s.entry.name}`}
                    items={[
                      s.entry.kind === 'design-system' && { id: 'try', label: trying?.id === s.id ? 'Stop trying it on' : 'Try it on in this browser' },
                      s.entry.kind === 'skill' && { id: 'evaluate', label: 'Evaluate…', disabled: s.evaluating?.state === 'pending' },
                      s.entry.kind === 'mcp' && { id: 'verify', label: 'Verify…', disabled: s.verifying?.state === 'pending' },
                      { id: 'decline', label: 'Decline…', danger: true, disabled: Boolean(busy) },
                    ]}
                    onAction={(id) => {
                      if (id === 'try') tryOnDesignSystem(trying?.id === s.id ? undefined : e);
                      else if (id === 'evaluate') setEvaluating(s);
                      else if (id === 'verify') setVerifying(s);
                      else setDeclining(s.id);
                    }}
                  />
                </>
              ) : undefined
            }
          >
            {s.entry.kind === 'skill' || s.entry.kind === 'mcp' || (s.scan && s.scan.level !== 'clean') ? (
              <div className="grid gap-2">
                {s.entry.kind === 'skill' && <SkillEvidence evaluation={s.evaluation} evaluating={s.evaluating} />}
                {s.entry.kind === 'mcp' && <ServerVerification verified={s.verified} verifying={s.verifying} declared={declaredTools(s.entry.content)} web={isWebService(s.entry.content)} />}
                <ScanFindings scan={s.scan} />
              </div>
            ) : null}
            {typeof s.entry.content === 'string' && <pre className="mono mt-2 max-h-40 overflow-auto rounded-lg bg-surface-secondary p-2 text-xs whitespace-pre-wrap">{s.entry.content}</pre>}
            {s.entry.kind === 'hook' && <div className="mono mt-2 rounded-lg bg-surface-secondary p-2 text-xs">{(s.entry.content as HookContent).command}</div>}
            {declining === s.id && (
              <div className="mt-2 grid gap-2 @[35rem]:flex @[35rem]:items-end">
                <label className="grid flex-1 gap-1 text-xs text-muted">
                  Why it is declined (the person reads this)
                  <input className={inputClass} value={reason} onChange={(ev) => setReason(ev.target.value)} autoFocus />
                </label>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onPress={() => setDeclining(undefined)}>
                    Cancel
                  </Button>
                  <Button size="sm" variant="danger" isDisabled={!reason.trim()} isPending={busy === s.id} onPress={() => void run(s.id, () => routes.declineSubmission(s.id, reason.trim()))}>
                    Decline
                  </Button>
                </div>
              </div>
            )}
          </ListRow>
        );
      })}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {evaluating && <EvaluateDialog target={{ kind: 'submission', id: evaluating.id, name: evaluating.entry.name }} onClose={() => setEvaluating(undefined)} onIssued={onChange} />}
      {verifying && <VerifyDialog target={{ kind: 'submission', id: verifying.id, name: verifying.entry.name }} web={isWebService(verifying.entry.content)} onClose={() => setVerifying(undefined)} onIssued={onChange} />}
    </ListGroup>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <FormField label={label}>{children}</FormField>;
}

/** Add or edit one customisation, in fields: a hook in words, never JSON. */
function Editor({ draft: d, setDraft, entries, markets, onSaved }: { draft: Draft; setDraft: (d: Draft | undefined) => void; entries: CatalogueEntry[]; markets: Array<{ name: string; label: string }>; onSaved: () => void }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const set = (patch: Partial<Draft>) => setDraft({ ...d, ...patch });
  const runtimes: Runtime[] = d.kind === 'hook' ? ['claude-code', 'copilot', 'codex'] : ['claude-code', 'copilot'];
  const toggle = (r: Runtime, on: boolean) => set({ runtimes: on ? [...new Set([...d.runtimes, r])] : d.runtimes.filter((x) => x !== r) });
  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      const id = d.id ?? newId(d, new Set(entries.map((e) => e.id)));
      await routes.saveCatalogue({ id, kind: d.kind, name: d.name.trim(), ...(d.summary.trim() ? { summary: d.summary.trim() } : {}), content: contentOf(d), ...(d.kind === 'agent' && fitOfDraft(d.fit) ? { fit: fitOfDraft(d.fit) } : {}) });
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSaving(false);
    }
  };
  const ready = d.name.trim() && (d.kind === 'hook' ? d.command.trim() && d.runtimes.length : d.kind === 'plugin' ? d.plugin.trim() && d.marketplace && d.runtimes.length : d.kind === 'marketplace' ? d.marketName.trim() && d.source.trim() && d.runtimes.length : d.kind === 'agent' ? d.description.trim() && d.body.trim() : d.body.trim());
  return (
    <FormSheet
      width="xl"
      title={d.id ? `Edit ${d.name || KIND_ONE[d.kind].toLowerCase()}` : 'Add a customisation'}
      description="Workbenches pick it up at their next refresh."
      onClose={() => setDraft(undefined)}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={() => setDraft(undefined)}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={saving} isDisabled={!ready} onPress={() => void save()}>
            Publish
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="What it is">
            <ChoiceSelect label="What it is" className="w-full" isDisabled={Boolean(d.id)} value={d.kind} options={KIND_ONE} onChange={(k) => setDraft({ ...blank(k), name: d.name, summary: d.summary })} />
          </Field>
          <Field label="Name">
            <input className={inputClass} value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder={d.kind === 'hook' ? 'Guard shell commands' : d.kind === 'plugin' ? 'Pull request review' : ''} />
          </Field>
        </div>
        <Field label="Summary (optional)">
          <input className={inputClass} value={d.summary} onChange={(e) => set({ summary: e.target.value })} />
        </Field>

        {d.kind === 'hook' && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="When it runs">
                <ChoiceSelect label="When it runs" className="w-full" value={d.event} options={HOOK_EVENT_WORDS} onChange={(event) => set({ event, ...(TOOL_EVENTS.includes(event) ? {} : { matcher: '' }) })} />
              </Field>
              {TOOL_EVENTS.includes(d.event) && (
                <Field label="Which tools (a pattern; empty for every tool)">
                  <input className={`${inputClass} mono`} value={d.matcher} onChange={(e) => set({ matcher: e.target.value })} placeholder="Bash|Edit" />
                </Field>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_9rem]">
              <Field label="Command">
                <input className={`${inputClass} mono`} value={d.command} onChange={(e) => set({ command: e.target.value })} placeholder="scripts/guard.sh" />
              </Field>
              <Field label="Time limit (seconds)">
                <input className={`${inputClass} mono`} inputMode="numeric" value={d.timeout} onChange={(e) => set({ timeout: e.target.value.replace(/\D/g, '') })} placeholder="60" />
              </Field>
            </div>
          </>
        )}

        {d.kind === 'plugin' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Plugin (its name in the marketplace)">
              <input className={`${inputClass} mono`} value={d.plugin} onChange={(e) => set({ plugin: e.target.value })} placeholder="pr-review-toolkit" />
            </Field>
            <Field label="From the marketplace">
              <ChoiceSelect label="From the marketplace" className="w-full" placeholder="Choose a marketplace" value={d.marketplace || undefined} options={Object.fromEntries(markets.map((m) => [m.name, `${m.label} (${m.name})`]))} onChange={(marketplace) => set({ marketplace })} />
              <p className="text-xs text-muted">Claude Code knows only claude-plugins-official on its own, and Copilot only awesome-copilot; for any other marketplace the workbench adds it on each machine before installing.</p>
            </Field>
          </div>
        )}

        {d.kind === 'marketplace' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Marketplace name (what plugins name)">
              <input className={`${inputClass} mono`} value={d.marketName} onChange={(e) => set({ marketName: e.target.value })} placeholder="acme-tools" />
            </Field>
            <Field label="Where it is (owner/repo, a git address or a marketplace.json address)">
              <input className={`${inputClass} mono`} value={d.source} onChange={(e) => set({ source: e.target.value })} placeholder="acme/plugins" />
            </Field>
          </div>
        )}

        {d.kind === 'agent' && (
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
            <Field label="What it does (its description)">
              <input className={inputClass} value={d.description} onChange={(e) => set({ description: e.target.value })} placeholder="Reviews a change before it is merged" />
            </Field>
            <Field label="Tools (optional, comma separated)">
              <input className={`${inputClass} mono`} value={d.tools} onChange={(e) => set({ tools: e.target.value })} placeholder="Read, Grep" />
            </Field>
            <Field label="Model (optional)">
              <input className={`${inputClass} mono`} value={d.model} onChange={(e) => set({ model: e.target.value })} placeholder="sonnet" />
            </Field>
          </div>
        )}
        {d.kind === 'agent' && <FitFields value={d.fit} onChange={(fit) => set({ fit })} />}
        {d.kind === 'instruction' && (
          <Field label="Applies to (optional: a file pattern; empty for everything)">
            <input className={`${inputClass} mono`} value={d.appliesTo} onChange={(e) => set({ appliesTo: e.target.value })} placeholder="**/*.ts" />
          </Field>
        )}
        {(d.kind === 'agent' || d.kind === 'instruction') && (
          <Field label={d.kind === 'agent' ? "The agent's instructions (Markdown)" : 'The instruction (Markdown)'}>
            <textarea className={`${inputClass} min-h-40`} value={d.body} onChange={(e) => set({ body: e.target.value })} />
          </Field>
        )}

        {d.kind !== 'agent' && d.kind !== 'instruction' && (
          <div className="grid gap-2">
            <span className="text-xs font-medium">Runtimes</span>
            <div className="flex flex-wrap gap-4">
              {runtimes.map((r) => (
                <Tick key={r} checked={d.runtimes.includes(r)} onChange={(on) => toggle(r, on)}>
                  {RUNTIME_TEXT[r]}
                </Tick>
              ))}
            </div>
            {d.kind === 'hook' && d.runtimes.includes('codex') && <p className="text-xs text-muted">{CODEX_NOTE}</p>}
          </div>
        )}
        {(d.kind === 'hook' || d.kind === 'plugin') && (
          <Field label="How it reaches people">
            <ChoiceSelect label="How it reaches people" className="w-full sm:w-80" value={d.delivery} options={DELIVERY_TEXT} onChange={(delivery) => set({ delivery })} />
          </Field>
        )}
        <ErrorNote error={error} />
      </div>
    </FormSheet>
  );
}

const count = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const whereLine = (m: CustomisationPresence) => `${m.hostname} (${m.workspaces.map((w) => `${w.name}${w.enabled === false ? ', turned off' : ''}`).join('; ')})`;

/** "on 3 machines" with the machines behind a disclosure, never one long joined line; a long list shows its first 25. */
function OnMachines({ machines }: { machines: CustomisationPresence[] }) {
  if (machines.length === 0) return null;
  return (
    <details className="block">
      <summary className="cursor-[var(--cursor-interactive)] text-muted">on {machines.length} {machines.length === 1 ? 'machine' : 'machines'}</summary>
      <ul className="mt-1 grid gap-0.5">
        {machines.slice(0, 25).map((m) => (
          <li key={m.deviceId} className="truncate" title={whereLine(m)}>
            {whereLine(m)}
          </li>
        ))}
        {machines.length > 25 ? <li className="text-muted">and {machines.length - 25} more</li> : null}
      </ul>
    </details>
  );
}

/** What each machine has: where each plugin and hook is, and what has not arrived yet. */
function Machines({ tone, v }: { tone: ArtTone; v: CustomisationsView | undefined }) {
  if (!v) return null;
  if (!v.machines.reporting)
    return <Empty tone={tone} icon={<Display />} title="No machine reports its customisations yet" body={v.machines.silent ? `${v.machines.silent} ${v.machines.silent === 1 ? 'machine runs' : 'machines run'} a workbench that does not say what it has. They show here once updated.` : 'Machines show here once a workbench is enrolled.'} />;
  return (
    <>
      {v.missing.length > 0 && (
        <ListGroup title="Not installed yet" count={v.missing.length} description="Installed on every machine, and not on these yet. A machine catches up at its next refresh.">
          {v.missing.map((m) => (
            <ListRow key={m.catalogueId} title={m.name} description={<details><summary className="cursor-[var(--cursor-interactive)]">Not on {m.machines.length} {m.machines.length === 1 ? 'machine' : 'machines'} yet</summary><ul className="mt-1 grid gap-0.5">{m.machines.slice(0, 25).map((x) => <li key={x.deviceId} className="truncate">{x.hostname}</li>)}{m.machines.length > 25 ? <li className="text-muted">and {m.machines.length - 25} more</li> : null}</ul></details>} status={<StatusMark state="medium" label="Waiting" />} />
          ))}
        </ListGroup>
      )}
      {v.plugins.length > 0 && (
        <ListGroup title="Where each plugin is" count={v.plugins.length}>
          {v.plugins.map((p) => (
            <ListRow
              key={p.id}
              title={<span className="mono break-all">{p.id}</span>}
              description={
                <>
                  <span className="block">{p.catalogueId ? 'Published' : "The person's own"}</span>
                  <OnMachines machines={p.machines} />
                </>
              }
            />
          ))}
        </ListGroup>
      )}
      {v.hooks.length > 0 && (
        <ListGroup title="Where each hook is" count={v.hooks.length}>
          {v.hooks.map((h) => (
            <ListRow
              key={`${h.event}|${h.matcher ?? ''}|${h.commandHash ?? ''}|${h.fromOrganisation}`}
              title={h.eventWords ?? h.event}
              description={
                <>
                  <span className="block">
                    {[h.matcher ? `for ${h.matcher}` : '', runtimesText(h.runtimes), h.fromOrganisation ? 'from the organisation' : "the person's own"].filter(Boolean).join(' · ')}
                  </span>
                  <OnMachines machines={h.machines} />
                </>
              }
            />
          ))}
        </ListGroup>
      )}
      {v.designSystems && v.designSystems.systems.length > 0 && v.designSystems.reporting > 0 && (
        <ListGroup title="Design system versions" count={v.designSystems.systems.length}>
          {v.designSystems.systems.map((d) => (
            <ListRow
              key={d.id}
              title={d.name}
              description={
                <>
                  <span className="block">{d.current ? `Current ${d.current}` : 'No longer published'}</span>
                  <span className="block tabular-nums">{spreadWords(d)}</span>
                </>
              }
              status={standing(d) === 'current' ? <StatusMark state="connected" label="Up to date" /> : standing(d) === 'behind' ? <StatusMark state="medium" label="Catching up" /> : undefined}
            />
          ))}
        </ListGroup>
      )}
      <EachMachine silent={v.machines.silent} />
    </>
  );
}

/** What each machine has, searched and paged on the server. */
function EachMachine({ silent }: { silent: number }) {
  const search = useListSearch();
  const list = usePagedList((cursor) => routes.customisationMachinesPage({ q: search.q, cursor }), { q: search.q }, { intervalMs: 60_000, keyOf: (m) => m.deviceId });
  const tone = VIEW_TONES.customisations!;
  return (
    <ListGroup
      title="Each machine"
      count={list.loaded ? listCount(list.items.length, { one: 'machine', many: 'machines' }, { total: search.q ? undefined : list.extra?.total, hasMore: list.hasMore, searched: search.q !== '' }) : undefined}
      description={silent ? `${silent} more on an older workbench that does not say what it has.` : undefined}
      actions={<SearchBox label="Search machines" value={search.typed} onChange={search.setTyped} placeholder="Search machines" className="w-40 max-w-full @lg:w-56" />}
      empty={list.loaded && list.items.length === 0 && search.q ? <NoMatch tone={tone} icon={<Display />} q={search.q} noun="machines" onClear={search.clear} /> : undefined}
    >
      {list.items.map((m) => (
          <ListRow
            key={m.deviceId}
            icon={<Display />}
            title={m.hostname}
            description={
              <>
                {m.workspaces.map((w) => (
                  <span key={w.id} className="block">
                    {w.name}: {w.plugins} {w.plugins === 1 ? 'plugin' : 'plugins'}, {w.hooks} {w.hooks === 1 ? 'hook' : 'hooks'}
                    {w.counts ? `, ${count(w.counts.agents, 'agent')}, ${count(w.counts.skills, 'skill')}, ${count(w.counts.prompts, 'prompt')}, ${count(w.counts.instructions, 'instruction')}` : ''}
                  </span>
                ))}
                {m.designSystems && m.designSystems.length > 0 ? <span className="block">Design systems: {m.designSystems.map((d) => `${d.name} ${d.version}${d.current ? '' : ' (an earlier version)'}`).join(', ')}</span> : null}
              </>
            }
            status={<StatusMark state={m.online ? 'online' : 'offline'} />}
          />
        ))}
      {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
    </ListGroup>
  );
}
